import { useState } from 'react';
import { Button, Drawer, DrawerBody, DrawerFooter, Icon } from '@paysera/ui';
import { useTranslation } from 'react-i18next';
import CartPlusIcon from '@app/components/icons/CartPlusIcon';
import DualRangeSlider from '@modules/investor/components/common/filter/DualRangeSlider';
import LoanPurpose from '@modules/investor/components/common/filter/LoanPurpose';
import { LOAN_STATUS_OPTIONS } from '@modules/investor/config/loanStatus';
import { OPPORTUNITIES_AMOUNT_FILTER, OPPORTUNITIES_PERIOD_FILTER } from '@modules/investor/config/opportunities';
import CreditScore from './CreditScore';
import InvestmentStatus from './InvestmentStatus';
const FilterPopup = ({ isMobile, onFilterChange, filters, showCreditScore = true, showInvestmentStatus = false, }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [period, setPeriod] = useState({
        min: (filters === null || filters === void 0 ? void 0 : filters.periodFrom) || OPPORTUNITIES_PERIOD_FILTER.min,
        max: (filters === null || filters === void 0 ? void 0 : filters.periodTo) || OPPORTUNITIES_PERIOD_FILTER.max,
    });
    const [amount, setAmount] = useState({
        min: (filters === null || filters === void 0 ? void 0 : filters.amountFrom) || OPPORTUNITIES_AMOUNT_FILTER.min,
        max: (filters === null || filters === void 0 ? void 0 : filters.amountTo) || OPPORTUNITIES_AMOUNT_FILTER.max,
    });
    const [purposeIds, setPurposeIds] = useState((filters === null || filters === void 0 ? void 0 : filters.purposeIds) || []);
    const [riskScore, setRiskScore] = useState((filters === null || filters === void 0 ? void 0 : filters.riskScores) || []);
    const [investmentStatuses, setInvestmentStatuses] = useState((filters === null || filters === void 0 ? void 0 : filters.investmentStatuses) || LOAN_STATUS_OPTIONS);
    const handleMinPeriodChange = (value) => {
        setPeriod((prevPeriod) => (Object.assign(Object.assign({}, prevPeriod), { min: value })));
    };
    const handleMaxPeriodChange = (value) => {
        setPeriod((prevPeriod) => (Object.assign(Object.assign({}, prevPeriod), { max: value })));
    };
    const handleMinAmountChange = (value) => {
        setAmount((prevAmount) => (Object.assign(Object.assign({}, prevAmount), { min: value })));
    };
    const handleMaxAmountChange = (value) => {
        setAmount((prevAmount) => (Object.assign(Object.assign({}, prevAmount), { max: value })));
    };
    const handleClose = () => {
        setIsOpen(false);
    };
    const handleReset = () => {
        setPeriod({
            min: OPPORTUNITIES_PERIOD_FILTER.min,
            max: OPPORTUNITIES_PERIOD_FILTER.max,
        });
        setAmount({
            min: OPPORTUNITIES_AMOUNT_FILTER.min,
            max: OPPORTUNITIES_AMOUNT_FILTER.max,
        });
        setPurposeIds([]);
        setRiskScore([]);
        setInvestmentStatuses(LOAN_STATUS_OPTIONS);
        if (onFilterChange) {
            onFilterChange({});
        }
        handleClose();
    };
    const handleApplyFilters = () => {
        if (onFilterChange) {
            onFilterChange({
                amountFrom: amount.min !== OPPORTUNITIES_AMOUNT_FILTER.min ? amount.min : undefined,
                amountTo: amount.max !== OPPORTUNITIES_AMOUNT_FILTER.max ? amount.max : undefined,
                periodFrom: period.min !== OPPORTUNITIES_PERIOD_FILTER.min ? period.min : undefined,
                periodTo: period.max !== OPPORTUNITIES_PERIOD_FILTER.max ? period.max : undefined,
                purposeIds: purposeIds.length ? purposeIds : undefined,
                riskScores: showCreditScore && riskScore.length ? riskScore : undefined,
                investmentStatuses: showInvestmentStatus &&
                    investmentStatuses.length > 0 &&
                    investmentStatuses.length < LOAN_STATUS_OPTIONS.length
                    ? investmentStatuses
                    : undefined,
            });
        }
        handleClose();
    };
    return (<>
            <Button size="sm" variant="tertiary" className={isMobile ? 'w-full' : ''} onClick={() => setIsOpen(true)} iconLeft={isMobile ? <CartPlusIcon /> : <Icon name="filter_list" size="sm"/>} label={t('financing.investing_page_table.filter')}/>
            <Drawer isOpen={isOpen} onClose={handleClose} title={t('financing.investing_page_table.filter_investments')} position="right" size={isMobile ? 'full' : 'md'}>
                <DrawerBody>
                    <div className="flex flex-col gap-6">
                        <div>
                            <h3 className="title-m text-neutral-primary-initial mb-4">
                                {t('financing.investing_page_table.loan_purpose')}
                            </h3>
                            <LoanPurpose value={purposeIds} onChange={setPurposeIds}/>
                        </div>
                        <div>
                            <h3 className="title-m text-neutral-primary-initial mb-4">
                                {t('financing.investing_page_table.period')}
                            </h3>
                            <DualRangeSlider preffix="period" minRange={OPPORTUNITIES_PERIOD_FILTER.min} maxRange={OPPORTUNITIES_PERIOD_FILTER.max} minValue={period.min} maxValue={period.max} step={1} label={t('financing.investing_page_table.period_in_months')} minLabel={t('financing.investing_page_table.from')} maxLabel={t('financing.investing_page_table.to')} onMinChange={handleMinPeriodChange} onMaxChange={handleMaxPeriodChange}/>
                        </div>
                        <div>
                            <h3 className="title-m text-neutral-primary-initial mb-4">
                                {t('financing.investing_page_table.amount')}
                            </h3>
                            <DualRangeSlider preffix="amount" minRange={OPPORTUNITIES_AMOUNT_FILTER.min} maxRange={OPPORTUNITIES_AMOUNT_FILTER.max} minValue={amount.min} maxValue={amount.max} step={10} label={t('financing.investing_page_table.loan_amount')} minLabel={t('financing.investing_page_table.from')} maxLabel={t('financing.investing_page_table.to')} onMinChange={handleMinAmountChange} onMaxChange={handleMaxAmountChange}/>
                        </div>
                        {showCreditScore && <CreditScore value={riskScore} onChange={setRiskScore}/>}
                        {showInvestmentStatus && (<div>
                                <h3 className="title-m text-neutral-primary-initial mb-4">
                                    {t('financing.investing_page_table.investment_status')}
                                </h3>
                                <InvestmentStatus value={investmentStatuses} onChange={setInvestmentStatuses}/>
                            </div>)}
                    </div>
                </DrawerBody>
                <DrawerFooter>
                    <div className="flex gap-2 justify-end">
                        <Button size="md" variant="tertiary" onClick={handleReset} iconLeft={<Icon name="refresh" size="sm"/>} label={t('financing.investing_page_table.reset_filters')}/>
                        <Button size="md" variant="primary" onClick={handleApplyFilters} label={t('financing.investing_page_table.apply')}/>
                    </div>
                </DrawerFooter>
            </Drawer>
        </>);
};
export default FilterPopup;
